import { PhoneOverride } from '@weave/schema-gen-ts/dist/schemas/phone/override/v2/override_service.pb';
import { Trans, useTranslation } from '@frontend/i18n';
import { useModalControl, ConfirmationModal, Text, useAlert } from '@frontend/design-system';
import { logError } from '../call-routes/helpers/utils';
import { PhoneOverrideType } from './types';
import { useOverrideMutations } from './use-overrides-mutations';

interface IOverrideSettingRemoveModal {
  override: PhoneOverride | PhoneOverrideType;
  modalProps: ReturnType<typeof useModalControl>['modalProps'];
  phoneTenantId?: string;
  onSuccess?: () => void;
}

export const OverrideSettingRemoveModal = ({
  override,
  modalProps,
  phoneTenantId,
  onSuccess,
}: IOverrideSettingRemoveModal) => {
  const { t } = useTranslation('phone', { keyPrefix: 'overrides' });
  const alerts = useAlert();

  const { remove } = useOverrideMutations(phoneTenantId ?? '');

  const deleteOverrideV2 = () =>
    remove.mutate(
      { phoneOverrideId: override?.id ?? '' },
      {
        onSuccess: () => {
          alerts.success(t('Override removed successfully'));
          onSuccess?.();
        },
        onError: (error) => {
          alerts.error(t('Override removal failed'));
          logError('Failed to remove override', 'OverrideSettingRemoveModal:deleteOverrideV2', {
            error,
            phoneTenantId,
            overrideId: override?.id,
          });
        },
      }
    );

  return (
    <ConfirmationModal
      {...modalProps}
      onConfirm={() => deleteOverrideV2()}
      title={t('Remove Active Override?')}
      message={
        <Trans t={t} callRouteName={(override as PhoneOverrideType).callRouteName}>
          Removing the override will return the Call Route{' '}
          <Text as='span' weight='bold'>
            {(override as PhoneOverrideType).callRouteName}
          </Text>{' '}
          to its standard configuration.
        </Trans>
      }
      cancelLabel={t('No, Cancel')}
      confirmLabel={t('Yes, Remove')}
      destructive
      cancelTrackingId='phone-portal-button-cancelRemoveOverride'
      confirmTrackingId='phone-portal-button-removeOverride'
    />
  );
};
